// Chrome kit 2 (DES1, prototype v4): the small shared pieces every app and
// artifact surface composes. Presentational only: every visible word comes
// from the caller, and state comes in as props. Styles live in kit.css and use
// only --pc-* tokens (tokens.css, surfaces.css).
import { useRef, type KeyboardEvent, type ReactNode } from 'react';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import { kitCopy } from '@/i18n/ns/kit';
import { cn } from '@/lib/utils';

export type ChipTone = 'neutral' | 'ink' | 'live' | 'draft' | 'inspect';

/** A small label: a binding, a kind, a "Preview" tag. Not interactive. */
export function Chip({
  tone = 'neutral',
  icon,
  className,
  children,
}: {
  tone?: ChipTone;
  icon?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <span data-slot="kit-chip" data-tone={tone} className={cn('pc-chip', className)}>
      {icon}
      <span className="pc-chip-text">{children}</span>
    </span>
  );
}

/** Who or what made something ("Built with Sonnet 5.5", "via Claude Code on
 *  Joakim's MacBook"). With `onOpen` it opens the run record. */
export function OriginTag({
  icon,
  label,
  onOpen,
}: {
  icon?: ReactNode;
  label: string;
  onOpen?: () => void;
}) {
  const copy = useNamespaceCopy(kitCopy);
  if (!onOpen)
    return (
      <span data-slot="kit-origin" className="pc-origin">
        {icon}
        <span>{label}</span>
      </span>
    );
  return (
    <button
      type="button"
      data-slot="kit-origin"
      className="pc-origin"
      title={copy.openRunRecord}
      onClick={onOpen}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

export type StatusTone = 'neutral' | 'success' | 'warning' | 'danger' | 'live';

/** A state in one or two words: "Published · v4", "Draft", "Refresh failed". */
export function StatusPill({
  tone = 'neutral',
  children,
}: {
  tone?: StatusTone;
  children: ReactNode;
}) {
  return (
    <span data-slot="kit-status" data-tone={tone} className="pc-status">
      {tone === 'live' ? <span className="pc-status-dot" aria-hidden="true" /> : null}
      {children}
    </span>
  );
}

export interface SegmentOption<T extends string> {
  value: T;
  label: string;
  /** A shorter label for phones (≤640 px), e.g. "Docs" for "Documents". */
  shortLabel?: string;
  icon?: ReactNode;
}

/** One choice of a few (Desktop / Tablet / Phone, Light / Dark). A radio group:
 *  arrow keys move and select, Home and End jump, one tab stop. */
export function Segmented<T extends string>({
  label,
  options,
  value,
  onChange,
  disabled = false,
}: {
  label: string;
  options: readonly SegmentOption<T>[];
  value: T;
  onChange: (next: T) => void;
  disabled?: boolean;
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const move = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const last = options.length - 1;
    const keys: Record<string, number> = {
      ArrowRight: index === last ? 0 : index + 1,
      ArrowDown: index === last ? 0 : index + 1,
      ArrowLeft: index === 0 ? last : index - 1,
      ArrowUp: index === 0 ? last : index - 1,
      Home: 0,
      End: last,
    };
    const next = keys[event.key];
    if (next === undefined) return;
    event.preventDefault();
    onChange(options[next].value);
    refs.current[next]?.focus();
  };
  return (
    <span role="radiogroup" aria-label={label} data-slot="kit-segmented" className="pc-segmented">
      {options.map((option, index) => {
        const checked = option.value === value;
        return (
          <button
            key={option.value}
            ref={(node) => {
              refs.current[index] = node;
            }}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={checked ? 0 : -1}
            disabled={disabled}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => move(event, index)}
          >
            {option.icon}
            {option.shortLabel ? (
              <>
                <span className="pc-segmented-long">{option.label}</span>
                <span className="pc-segmented-short">{option.shortLabel}</span>
              </>
            ) : (
              <span>{option.label}</span>
            )}
          </button>
        );
      })}
    </span>
  );
}

/** A placeholder shape while content loads. Group skeletons in SkeletonBlock
 *  so assistive technology hears one "Loading", not every bar. */
export function Skeleton({
  width,
  height,
  shape = 'line',
}: {
  width?: string;
  height?: string;
  shape?: 'line' | 'block' | 'circle';
}) {
  return (
    <span
      aria-hidden="true"
      data-slot="kit-skeleton"
      data-shape={shape}
      className="pc-skeleton"
      style={{ width, height }}
    />
  );
}

export function SkeletonBlock({ children }: { children: ReactNode }) {
  const copy = useNamespaceCopy(kitCopy);
  return (
    <div role="status" aria-label={copy.loading} data-slot="kit-skeleton-block">
      {children}
    </div>
  );
}

/** Determinate progress ("Matched 31 of 40"). Indeterminate work uses the
 *  build line's narration instead of a bar that pretends to know. */
export function Progress({ done, total, label }: { done: number; total: number; label: string }) {
  const copy = useNamespaceCopy(kitCopy);
  const safeTotal = total > 0 ? total : 1;
  const clamped = Math.min(Math.max(done, 0), safeTotal);
  return (
    <span
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={safeTotal}
      aria-valuenow={clamped}
      aria-valuetext={formatCopy(copy.progressOf, { done: clamped, total: safeTotal })}
      data-slot="kit-progress"
      className="pc-progress"
    >
      <span className="pc-progress-fill" style={{ transform: `scaleX(${clamped / safeTotal})` }} />
    </span>
  );
}

export interface StackPerson {
  id: string;
  name: string;
  initials: string;
}

/** Faces for an audience ("Sara Nilsson, Omar Haddad and 4 others"). Shows up
 *  to `max` faces and a count; the accessible name lists the shown names. */
export function AvatarStack({ people, max = 5 }: { people: readonly StackPerson[]; max?: number }) {
  const copy = useNamespaceCopy(kitCopy);
  const shown = people.slice(0, Math.max(1, max));
  const rest = people.length - shown.length;
  const names = shown.map((person) => person.name).join(', ');
  const label = rest > 0 ? `${names} ${formatCopy(copy.othersLabel, { count: rest })}` : names;
  return (
    <span role="img" aria-label={label} data-slot="kit-avatars" className="pc-avatars">
      {shown.map((person) => (
        <span key={person.id} className="pc-avatar" aria-hidden="true">
          {person.initials}
        </span>
      ))}
      {rest > 0 ? (
        <span className="pc-avatar pc-avatar-more" aria-hidden="true">
          +{rest}
        </span>
      ) : null}
    </span>
  );
}

/** Nothing here yet, said plainly, with the next step. */
export function EmptyState({
  icon,
  title,
  body,
  actions,
}: {
  icon?: ReactNode;
  title: string;
  body?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div data-slot="kit-empty" className="pc-empty">
      {icon ? (
        <span className="pc-empty-icon" aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <p className="pc-empty-title">{title}</p>
      {body ? <div className="pc-empty-body">{body}</div> : null}
      {actions ? <div className="pc-empty-actions">{actions}</div> : null}
    </div>
  );
}
